import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { EmptyState } from '@/components/ui/empty-state';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';
import { fallback } from '@/lib/utils/zodFallback';
import { InventoryStockTable } from '../../../-components/InventoryStockTable';
import { useInventoryComponentsList } from '../../../-hooks/useInventoryComponentsList';

const searchSchema = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 20),
  sort: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/inventory/(pages)/(list)/components/',
)({
  validateSearch: searchSchema,
  component: InventoryComponentsPage,
});

function buildFilter(filters: Record<string, string[]>): string {
  const flat: Record<string, string> = {};
  for (const [key, vals] of Object.entries(filters)) {
    const v = vals[0];
    if (vals.length > 0 && v !== undefined) flat[key] = v;
  }
  return Object.keys(flat).length ? JSON.stringify(flat) : '';
}

function InventoryComponentsPage() {
  const { getTranslation: t } = useTranslator({ page: 'inventory' });
  const table = useTable('inventory-components');
  const filter = buildFilter(table.filters);

  const { data, isFetching, isError } = useInventoryComponentsList({
    page: table.page,
    limit: table.limit,
    ...(table.queryParams.sort ? { sort: table.queryParams.sort } : {}),
    ...(filter ? { filter } : {}),
  });

  if (isError) {
    return (
      <EmptyState
        title={t('error.title')}
        description={t('error.description')}
      />
    );
  }

  const items = data?.data ?? [];
  const totalCount = data?.meta?.totalCount ?? 0;

  return (
    <InventoryStockTable
      data={items}
      isFetching={isFetching}
      totalCount={totalCount}
      table={table}
      storageKey='inventory-components'
    />
  );
}
